import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { AppModel } from "../../../app/app-model";
import { useSupportedDiagnoses } from "../../../catalogs/supported-diagnoses";
import { AddTable } from "../../../components/ui-kit/tables/add-table";
import { CellCombobox } from "../../../components/ui-kit/tables/cell-fields/cell-combobox";
import { CellDateInput } from "../../../components/ui-kit/tables/cell-fields/cell-date-input";
import { CellTextInput } from "../../../components/ui-kit/tables/cell-fields/cell-text-input";
import { CellRemoveButton } from "../../../components/ui-kit/tables/cells/cell-remove-button";
import { getNamesValues, getVariableByName } from "../../../utils/data-utils";
import { CellGenericValueField } from "../common/cell-generic-value-field";
import { AddDiags } from "./add-diags";

export const AddDiagsTable = observer(function AddDiagsTable(props: {
  app: AppModel;
  model?: AddDiags;
  onSaved?: () => void;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const supportedDiags = useSupportedDiagnoses();
  const [model] = useState(
    () => props.model ?? new AddDiags(patient.api, patient),
  );

  useEffect(() => {
    if (!props.model) {
      void model.initEmpty();
    }
  }, [model, props.model]);

  return (
    <AddTable
      model={model}
      onSaved={props.onSaved}
      setAllDates="date"
      columns={[
        {
          header: <Trans>Name</Trans>,
          mandatory: true,
          cell: (item) => (
            <CellCombobox
              attr="diagnosis_name"
              model={item}
              values={getNamesValues(supportedDiags)}
              listTitle={t`Select diagnosis`}
            />
          ),
        },
        {
          header: <Trans>Value</Trans>,
          mandatory: true,
          cell: (item) => (
            <CellGenericValueField
              attr="diagnosis_value"
              model={item}
              variableDef={getVariableByName(
                supportedDiags,
                item.diagnosis_name,
              )}
            />
          ),
        },
        {
          header: <Trans>Diagnosed at</Trans>,
          cell: (item) => (
            <CellDateInput attr="diagnosed_at" model={item} type="date" />
          ),
        },
        {
          header: <Trans>Remission</Trans>,
          cell: (item) => (
            <CellDateInput attr="remission" model={item} type="date" />
          ),
        },
        {
          header: <Trans>Comment</Trans>,
          cell: (item) => (
            <CellTextInput attr="comment" model={item} multiline="autosize" />
          ),
        },
        {
          header: <Trans>Remove diagnosis</Trans>,
          cell: (item, index) => (
            <CellRemoveButton index={index} model={model} />
          ),
        },
      ]}
    />
  );
});
